/**
 * When the Tag Register should fetch a page after its table options change.
 *
 * The table options object (`TAGS_REGISTER.options`) is shared by the pager, the sort headers,
 * the page-size selector and the toolbar search box, and the deep watcher on it cannot tell
 * which one moved. Typing must wait for a pause; a click must not (it used to pay the same
 * 500 ms debounce before its request was even sent). The table also writes the unchanged
 * query back through `:options.sync` when it mounts, which must not cost a second request.
 * See .claude/features/TAG_REGISTER_PERF_BASELINE.md.
 */

export const REGISTER_LOAD = Object.freeze({
  NONE: 'none',
  DEBOUNCE: 'debounce',
  NOW: 'now',
});

/** Options that change which rows the server returns, apart from the typed search. */
const CLICK_DRIVEN_KEYS = ['page', 'itemsPerPage', 'sortBy', 'sortDesc'];

/**
 * @param {Object} options Table options (`page`, `itemsPerPage`, `sortBy`, `sortDesc`, `search`, ...).
 * @returns {{ clicks: string, search: string }} Comparable form of the query the options describe.
 */
export function registerOptionsSnapshot(options = {}) {
  // The store starts without `page`; the table adds `page: 1` on mount. Both ask for page 1.
  const normalized = { ...options, page: options.page ?? 1 };
  return {
    clicks: JSON.stringify(CLICK_DRIVEN_KEYS.map((key) => normalized[key] ?? null)),
    search: options.search || '',
  };
}

/**
 * @param {{ clicks: string, search: string } | null} previous Snapshot of the last handled options.
 * @param {{ clicks: string, search: string }} next Snapshot of the options now.
 * @returns {string} One of REGISTER_LOAD.
 */
export function decideRegisterOptionsLoad(previous, next) {
  if (!previous) return REGISTER_LOAD.NOW;
  // Checked first: the search box also resets `page` to 1, and that is still typing.
  if (previous.search !== next.search) return REGISTER_LOAD.DEBOUNCE;
  if (previous.clicks !== next.clicks) return REGISTER_LOAD.NOW;
  return REGISTER_LOAD.NONE;
}

/**
 * Column filters as the API expects them (`{ column: value }`), dropping empty ones.
 * @param {Object} columnFilters `{ column: { type: 'search'|'select', value } }` from the store.
 * @returns {Object}
 */
export function columnFilterParams(columnFilters = {}) {
  return Object.fromEntries(
    Object.entries(columnFilters)
      .filter(([, filter]) => filter && filter.value !== null && filter.value !== undefined
        && String(filter.value).trim() !== '')
      .map(([column, filter]) => [column, filter.value]),
  );
}

/**
 * True when a request failed only because a newer one aborted it. httpService re-rejects
 * aborts as `{ ...error }`, so only own properties (name, code, __CANCEL__) can be relied on.
 * @param {unknown} err
 * @returns {boolean}
 */
export function isRequestCancelled(err) {
  if (!err) return false;
  return err.name === 'CanceledError'
    || err.name === 'AbortError'
    || err.code === 'ERR_CANCELED'
    || err.__CANCEL__ === true;
}
